/**
 * The boot probe. Only the cloud answers `/vms/api/v1/config`, and without its answer the page
 * shows the boot error page instead of the app.
 */

import type { ClientConfig } from '@/types/api';
import { ClientConfigSchema } from '@/types/api';
import { ViolationSourceKind } from '@/types/contract';

import { getAnswerOrigin, JsonTextSchema, parseOne } from './contract';

export const CONFIG_URL = '/vms/api/v1/config';

/** The cloud's client config, or null where no cloud answers. */
export async function probeCloudConfig(): Promise<ClientConfig | null> {
    try {
        const resp = await fetch(CONFIG_URL, { credentials: 'include' });
        const contentType = resp.headers.get('Content-Type') ?? '';
        // A proxy error is not 2xx, and a page served in place of the API is not JSON;
        // neither is the cloud.
        if (!resp.ok || !contentType.includes('application/json')) {
            return null;
        }
        const source = { kind: ViolationSourceKind.Cloud } as const;
        const origin = getAnswerOrigin(CONFIG_URL, resp.status, source);
        const body = parseOne(JsonTextSchema, await resp.text(), origin);
        return parseOne(ClientConfigSchema, body, origin);
    } catch {
        // parseOne has reported a broken config; the page shows the boot error page.
        return null;
    }
}
